/**
 * 43. Price Lists & Discounts — price list editor; rules; discount
 * approval thresholds.
 *
 * The currency filter offers the currencies the loaded price lists are in,
 * and filters them (it used to offer two hard-coded codes and filter
 * nothing, CUR-2).
 *
 * Arabic and English (C7): every word from the catalogs; a discount is a
 * percentage the locale writes.
 */
import { useState } from "react";
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Toolbar,
  Field,
  Select,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { formatMoney, formatPercent, formatQuantity } from "@/lib/money";

interface PriceListRow {
  id: string;
  name: string;
  code: string;
  currencyCode: string;
  isActive: boolean;
  itemCount: number;
}

interface DiscountRow {
  id: string;
  name: string;
  discountPercent: string;
  minAmount: string;
  currencyCode: string;
  approvalThreshold: string | null;
  status: "Active" | "Draft" | "Superseded";
}

export function PriceListsPage() {
  const { data: priceLists, isLoading } = useApiList<PriceListRow>(
    ["sales", "price-lists"],
    "/sales/price-lists",
  );
  const { data: discounts } = useApiList<DiscountRow>(
    ["sales", "discounts"],
    "/sales/discounts",
  );
  const { t } = useTranslation();
  const [currency, setCurrency] = useState("");
  const currencies = [
    ...new Set((priceLists?.items ?? []).map((r) => r.currencyCode)),
  ].sort();
  const shown = (priceLists?.items ?? []).filter(
    (r) => !currency || r.currencyCode === currency,
  );

  return (
    <div>
      <PageHeader
        title={t("sales.priceLists.title")}
        subtitle={t("sales.priceLists.subtitle")}
        actions={
          <PendingButton variant="primary" reason="pricing" slice="CRM-B14">
            {t("sales.priceLists.new")}
          </PendingButton>
        }
      />
      <Toolbar>
        <Field label={t("money.currency")}>
          <Select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          >
            <option value="">{t("money.allCurrencies")}</option>
            {currencies.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </Select>
        </Field>
      </Toolbar>
      {isLoading || !priceLists ? (
        <Skeleton rows={8} />
      ) : (
        <Card
          title={listTitle(
            t("sales.priceLists.list"),
            currency ? shown.length : priceLists.total,
          )}
        >
          <DataTable<PriceListRow>
            rows={shown}
            columns={[
              { key: "name", label: t("fields.name") },
              {
                key: "code",
                label: t("fields.code"),
                render: (r) => <span className="atlas-mono">{r.code}</span>,
              },
              { key: "currencyCode", label: t("fields.currency") },
              {
                key: "itemCount",
                label: t("sales.priceLists.items"),
                numeric: true,
                render: (r) => formatQuantity(r.itemCount, 0),
              },
              {
                key: "isActive",
                label: t("fields.active"),
                render: (r) => (r.isActive ? t("shared.yes") : t("shared.no")),
              },
            ]}
          />
        </Card>
      )}
      <div className="mt-4">
        <Card
          title={listTitle(
            t("sales.priceLists.discounts"),
            discounts?.total ?? 0,
          )}
        >
          <DataTable<DiscountRow>
            rows={discounts?.items ?? []}
            columns={[
              { key: "name", label: t("fields.name") },
              {
                key: "discountPercent",
                label: t("sales.priceLists.discount"),
                render: (r) => formatPercent(r.discountPercent),
              },
              {
                key: "minAmount",
                label: t("sales.priceLists.minAmount"),
                numeric: true,
                render: (r) => formatMoney(r.minAmount, r.currencyCode),
              },
              {
                key: "approvalThreshold",
                label: t("sales.priceLists.approvalThreshold"),
                render: (r) =>
                  r.approvalThreshold
                    ? formatMoney(r.approvalThreshold, r.currencyCode)
                    : "—",
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <StatusBadge status={r.status} type="Discount" />
                ),
              },
            ]}
          />
        </Card>
      </div>
    </div>
  );
}
